import axios from 'axios';
import { Keys, type PostKeys, type PostSignUp } from '@/models/login';
import { LocalStorageUtils } from '@/utils';
import { apiClient, BACKEND_URL, signedHeaders } from './apiClient';

/** How long sign-out waits on the API to revoke the key before clearing it from the browser anyway. */
export const REVOKE_TIMEOUT_MS = 5_000;

class LoginService {

  initService() {}

  async loginUser(keysData: PostKeys) {
    // Unsigned by definition — this is the call that fetches the keys everything else signs with.
    const uri = `/keys/oauth`;
    const res = await apiClient.post(uri, keysData);
    return Keys.mapKeys(res.data);
  }

  async signUp(signUpData: PostSignUp) {
    const uri = `/user`;
    const res = await apiClient.post(uri, signUpData);
    return res.data;
  }

  /**
   * Every sign-out the person starts (ENG-10329): the logout route, which the sidebar, onboarding and
   * the deactivated page all reach, and sign-up's "Logout". It asks the API to revoke the key first, so
   * a copy of it taken before sign-out stops working, then clears the keys and the open study.
   *
   * Never rejects and never waits longer than `REVOKE_TIMEOUT_MS`: a revoke that fails, is refused or
   * stalls is dropped and the keys are cleared all the same. An expired session doesn't come here,
   * because the API has already refused its key; `main.ts`'s `onSessionExpired` only clears.
   */
  async signOut() {
    await this.revokeCurrentKey();
    LocalStorageUtils.logoutUser();
  }

  /**
   * `DELETE keys/current`, signed with the key it revokes. Through bare `axios` rather than
   * `apiClient`: a key that has already expired gets a 401, and the interceptor would answer that by
   * sending someone who is signing out to the logout route a second time, as an expired session.
   */
  private async revokeCurrentKey() {
    if (!LocalStorageUtils.getUserKeys()) {
      return;
    }
    const uri = 'keys/current';
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<void>(resolve => {
      timer = setTimeout(() => {
        controller.abort();
        resolve();
      }, REVOKE_TIMEOUT_MS);
    });
    try {
      const revoked = axios.delete(`${BACKEND_URL}/${uri}`, {
        headers: signedHeaders('DELETE', uri),
        signal: controller.signal
      });
      await Promise.race([revoked, timedOut]);
    } catch {
      // Offline, refused or already expired: the person is signing out either way.
    } finally {
      clearTimeout(timer);
    }
  }
}

export default new LoginService();
